<template>
  <div>
    <v-tooltip top>
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          icon
          color="primary"
          @click="openModal"
          :disabled="readonly"
          class="camera-plus-btn"
          small
          v-on="on"
          v-bind="attrs"
        >
          <v-icon>mdi-plus</v-icon>
        </v-btn>
      </template>
      <span> {{$t('message.checklist.uploadDocument')}}</span>
    </v-tooltip>

    <v-dialog v-model="dialog" max-width="500" persistent>
      <v-card>
        <v-card-title class="d-flex align-center justify-space-between">
          <span>{{ label }}</span>
          <v-btn icon @click="closeModal">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <v-btn
            v-if="!showCamera && !capturedImage"
            block
            color="primary"
            @click="startCamera"
            :disabled="readonly"
          >
            <v-icon left>mdi-camera</v-icon>
            {{$t('message.checklist.openCamera')}}
          </v-btn>

          <video
            v-if="showCamera"
            ref="video"
            autoplay
            playsinline
            width="100%"
            class="my-3 camera-preview"
          ></video>

          <canvas
            ref="canvas"
            style="display:none"
          ></canvas>

          <v-img
            v-if="capturedImage"
            :src="capturedImage"
            max-width="100%"
            class="my-3"
            contain
          />

          <v-btn
            v-if="showCamera"
            block
            color="success"
            class="mb-2"
            @click="capturePhoto"
          >
            <v-icon left>mdi-camera-iris</v-icon>
            {{$t('message.checklist.takePhoto')}}
          </v-btn>

          <v-btn
            v-if="capturedImage"
            block
            color="warning"
            class="mb-2"
            @click="retakePhoto"
          >
            <v-icon left>mdi-refresh</v-icon>
            {{$t('message.checklist.retake')}}
          </v-btn>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeModal">
            {{ $t('message.common.cancel') }}
          </v-btn>
          <v-btn
            color="primary"
            :disabled="!capturedImage"
            @click="uploadPhoto"
          >
            {{$t('message.common.save')}}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: 'CameraUploadModal',
  props: {
    readonly: { type: Boolean, default: false },
    label: { type: String, default: 'Capture Photo' },
    filePrefix: { type: String, default: 'photo' }
  },
  data () {
    return {
      dialog: false,
      showCamera: false,
      capturedImage: null,
      stream: null
    }
  },
  methods: {
    openModal () {
      this.dialog = true
      this.startCamera()
    },
    closeModal () {
      this.dialog = false
      this.stopCamera()
      this.showCamera = false
      this.capturedImage = null
      this.$emit('close')
    },
    async startCamera () {
      try {
        this.capturedImage = null
        this.showCamera = true
        this.stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: 'environment' }
          },
          audio: false
        })

        this.$nextTick(() => {
          if (this.$refs.video) {
            this.$refs.video.srcObject = this.stream
          }
        })
      } catch (error) {
        this.showCamera = false
        this.$root.$emit('snackbar', {
          snackbar: true,
          color: 'error',
          text: 'Unable to access camera'
        })
      }
    },
    capturePhoto () {
      const video = this.$refs.video
      const canvas = this.$refs.canvas

      if (!video || !canvas) return

      canvas.width = video.videoWidth || 1280
      canvas.height = video.videoHeight || 720

      const ctx = canvas.getContext('2d')
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height)

      this.capturedImage = canvas.toDataURL('image/png')
      this.stopCamera()
      this.showCamera = false
    },
    retakePhoto () {
      this.capturedImage = null
      this.startCamera()
    },
    stopCamera () {
      if (this.stream) {
        this.stream.getTracks().forEach(track => track.stop())
        this.stream = null
      }
    },
    uploadPhoto () {
      if (!this.capturedImage) return

      const arr = this.capturedImage.split(',')
      const mime = arr[0].match(/:(.*?);/)[1]
      const bstr = atob(arr[1])
      let n = bstr.length
      const u8arr = new Uint8Array(n)

      while (n--) {
        u8arr[n] = bstr.charCodeAt(n)
      }

      const fileName = `${this.filePrefix}-${Date.now()}.png`
      const file = new File([u8arr], fileName, { type: mime })

      this.$emit('upload-success', file)
      this.closeModal()
    }
  },
  beforeDestroy () {
    this.stopCamera()
  }
}
</script>

<style scoped>
.camera-preview {
  border-radius: 8px;
  background: #000;
}
.camera-plus-btn {
  margin-top: 4px;
}
</style>
